import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, Instagram, Facebook, MapPin, Send, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Toast } from '@/components/ui/toast';

export function ContactPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [showToast, setShowToast] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    setTimeout(() => {
      setLoading(false);
      setShowToast(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };

  return (
    <div className="bg-gray-50 min-h-screen py-8 sm:py-12">
      {showToast && (
        <Toast
          type="success"
          title="Message envoyé avec succès!"
          message="Nous vous répondrons dans les plus brefs délais."
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <Link to="/">
            <Button variant="ghost" className="flex items-center text-gray-600">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour à l'accueil
            </Button>
          </Link>
        </div>

        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            Contactez-nous
          </h1>
          <p className="mt-2 sm:mt-4 text-base sm:text-xl text-gray-500">
            Nous sommes là pour vous aider. N'hésitez pas à nous contacter.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-primary p-6 sm:p-8 text-white">
              <h2 className="text-xl sm:text-2xl font-bold">Informations de contact</h2>
              <p className="mt-2 text-white/80">
                Contactez-nous par email, téléphone ou sur les réseaux sociaux
              </p>
            </div>
            
            <div className="p-6 sm:p-8 space-y-5 sm:space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                </div>
                <div className="ml-3 sm:ml-4">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900">Email</h3>
                  <p className="mt-1 text-gray-600">
                    <a href="mailto:aemfamily509@gmail.com" className="hover:text-primary">
                      aemfamily509@gmail.com
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                </div>
                <div className="ml-3 sm:ml-4">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900">Téléphone</h3>
                  <div className="mt-1 space-y-1">
                    <p className="text-gray-600">
                      <a href="tel:+15853629020" className="hover:text-primary">
                        +1 (585) 362-9020
                      </a>
                    </p>
                    <p className="text-gray-600">
                      <a href="tel:+50947734531" className="hover:text-primary">
                        +509 4773-4531
                      </a>
                    </p>
                    <p className="text-gray-600">
                      <a href="tel:+50941398657" className="hover:text-primary">
                        +509 4139-8657
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                </div>
                <div className="ml-3 sm:ml-4">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900">Adresse</h3>
                  <p className="mt-1 text-gray-600">
                    Port-au-Prince, Haïti
                  </p>
                </div>
              </div>

              <div className="pt-4 sm:pt-6 border-t border-gray-200">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Suivez-nous</h3>
                <div className="mt-3 sm:mt-4 flex space-x-5 sm:space-x-6">
                  <a 
                    href="https://www.instagram.com/aem_family509?igsh=MWhtc2Jsc3hxY21zbQ==" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-primary"
                  >
                    <span className="sr-only">Instagram</span>
                    <Instagram className="h-6 w-6" />
                  </a>
                  <a 
                    href="https://www.facebook.com/profile.php?id=61573641493655" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-primary"
                  >
                    <span className="sr-only">Facebook</span>
                    <Facebook className="h-6 w-6" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Envoyez-nous un message</h2>
              <p className="mt-2 text-gray-600">
                Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.
              </p>

              <form onSubmit={handleSubmit} className="mt-6 sm:mt-8 space-y-5 sm:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                    Sujet
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="general">Question générale</option>
                    <option value="support">Support technique</option>
                    <option value="partnership">Partenariat</option>
                    <option value="feedback">Commentaires</option>
                    <option value="other">Autre</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Décrivez votre demande en détail..."
                  />
                </div>

                <div>
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full flex justify-center items-center"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    ) : (
                      <Send className="h-5 w-5 mr-2" />
                    )}
                    Envoyer le message
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-8 sm:mt-12 bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Notre emplacement</h2>
            <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d120927.45011390086!2d-72.40242221249999!3d18.547464900000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8eb9e7963a3bfa01%3A0x3ae3e0e221f13bed!2sPort-au-Prince%2C%20Haiti!5e0!3m2!1sen!2sus!4v1650000000000!5m2!1sen!2sus" 
                width="100%" 
                height="450" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="AEM Marketplace Location"
                className="rounded-lg"
              ></iframe>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-8 sm:mt-12 bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8">Questions fréquemment posées</h2>
            
            <div className="space-y-5 sm:space-y-6">
              <div className="border-b border-gray-200 pb-5 sm:pb-6">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Quels sont vos horaires d'ouverture?</h3>
                <p className="mt-2 text-sm sm:text-base text-gray-600">Notre service client est disponible du lundi au vendredi de 8h à 18h (heure locale d'Haïti).</p>
              </div>
              
              <div className="border-b border-gray-200 pb-5 sm:pb-6">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Combien de temps pour recevoir une réponse?</h3>
                <p className="mt-2 text-sm sm:text-base text-gray-600">Nous nous efforçons de répondre à toutes les demandes dans un délai de 24 à 48 heures ouvrables.</p>
              </div>
              
              <div className="border-b border-gray-200 pb-5 sm:pb-6">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Comment puis-je devenir partenaire?</h3>
                <p className="mt-2 text-sm sm:text-base text-gray-600">Pour devenir partenaire, veuillez nous contacter par email à aemfamily509@gmail.com avec l'objet "Demande de partenariat" et nous vous fournirons toutes les informations nécessaires.</p>
              </div>
              
              <div className="pb-5 sm:pb-6">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Proposez-vous un support technique?</h3>
                <p className="mt-2 text-sm sm:text-base text-gray-600">Oui, notre équipe technique est disponible pour vous aider avec tout problème que vous pourriez rencontrer sur notre plateforme. Contactez-nous par email ou téléphone.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}